import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ChevronDown, ShoppingCart } from "lucide-react";
import { Logo } from "./Logo";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { SHOP_LINKS, CATALOG_URL } from "@/lib/data";
import { useCart } from "@/lib/cart";
import { useSettings } from "@/lib/settings";
import { api } from "@/lib/api";

const BASE_NAV = [
  { label: "HOME", to: "/" },
  { label: "SHOP", to: "/shop", menu: SHOP_LINKS.map((s) => ({ label: s, to: "/shop" })) },
  { label: "CATALOG", href: CATALOG_URL, external: true },
  { label: "SERVICES", to: "/services" },
  { label: "GALLERY", to: "/gallery" },
  { label: "ABOUT US", to: "/about" },
  { label: "CONTACT", to: "/contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [shops, setShops] = useState([]);
  const { pathname } = useLocation();
  const { count, setOpen: setCartOpen } = useCart();
  const { settings } = useSettings();
  const overlay = pathname === "/";

  useEffect(() => {
    api.get("/store/groups").then(({ data }) => setShops(data)).catch(() => {});
  }, []);

  const storeName = (settings.store_name || "Spirit Store").toUpperCase();
  const shopMenu = shops.map((g) => ({ label: g.name.toUpperCase(), to: `/spirit-store?shop=${encodeURIComponent(g.name)}` }));
  const storeItem = shops.length
    ? { label: "SPIRIT SHOPS", to: "/spirit-store", menu: shopMenu }
    : { label: storeName, to: "/spirit-store" };
  const NAV = settings.store_active !== false
    ? [BASE_NAV[0], BASE_NAV[1], storeItem, ...BASE_NAV.slice(2)]
    : BASE_NAV;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || !overlay;
  const textColor = solid ? "text-ink" : "text-[#f2f0ea]";

  return (
    <header
      className={`relative transition-all duration-500 ${
        solid ? "bg-paper/95 backdrop-blur-md shadow-[0_1px_0_rgba(0,0,0,0.08)]" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className={`max-w-[1280px] mx-auto px-5 lg:px-8 ${overlay ? "h-[120px]" : "h-[92px]"} flex items-center justify-between transition-all duration-500`}>
        <Link to="/" data-testid="nav-logo-link">
          <Logo light={!solid} size={overlay ? "xl" : "lg"} />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((item) => (
            <div key={item.label} className="relative group">
              {item.external ? (
                <a
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`flex items-center gap-1 text-[12px] tracking-[0.18em] font-semibold ${textColor} link-underline`}
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`flex items-center gap-1 text-[12px] tracking-[0.18em] font-semibold ${textColor} link-underline ${
                    pathname === item.to ? "text-gold" : ""
                  }`}
                >
                  {item.label}
                  {item.menu && <ChevronDown size={13} className="mt-0.5" />}
                </Link>
              )}
              {item.menu && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300">
                  <div className="bg-ink text-[#f2f0ea] min-w-[180px] py-3 shadow-2xl max-h-[70vh] overflow-y-auto">
                    {item.menu.map((m) => (
                      <Link key={m.label} to={m.to} className="block px-5 py-2 text-[12px] tracking-[0.12em] hover:text-gold hover:pl-7 transition-all">
                        {m.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <button
            onClick={() => setCartOpen(true)}
            data-testid="nav-cart-button"
            aria-label="Open cart"
            className={`relative ${textColor} hover:text-gold transition-colors`}
          >
            <ShoppingCart size={21} />
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-2 -right-2 bg-gold text-ink text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1">{count}</span>
            )}
          </button>
          <Link
            to="/quote"
            data-testid="nav-quote-btn"
            className={`px-7 py-3 text-[12px] font-bold tracking-[0.16em] transition-colors ${
              solid ? "bg-ink text-[#f2f0ea] hover:bg-navy" : "bg-[#f2f0ea] text-ink hover:bg-gold"
            }`}
          >
            GET A QUOTE
          </Link>
        </div>

        {/* Mobile */}
        <div className="lg:hidden flex items-center gap-4">
          <button onClick={() => setCartOpen(true)} data-testid="mobile-nav-cart-button" aria-label="Open cart" className={`relative ${textColor}`}>
            <ShoppingCart size={22} />
            {count > 0 && <span className="absolute -top-2 -right-2 bg-gold text-ink text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1">{count}</span>}
          </button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className={textColor} aria-label="Menu" data-testid="nav-mobile-toggle"><Menu size={24} /></button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-ink text-[#f2f0ea] border-none w-[300px]">
              <div className="mt-6 flex flex-col gap-1">
                <Logo light className="mb-8" />
                {NAV.map((item) => (
                  <div key={item.label}>
                    {item.external ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                        className="block py-3 text-lg font-display tracking-[0.12em] border-b border-white/10 hover:text-gold transition-colors"
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link
                        to={item.to}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                        className="block py-3 text-lg font-display tracking-[0.12em] border-b border-white/10 hover:text-gold transition-colors"
                      >
                        {item.label}
                      </Link>
                    )}
                    {item.menu?.map((m) => (
                      <Link
                        key={m.label}
                        to={m.to}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-sub-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="block py-2 pl-6 text-sm font-display tracking-[0.12em] text-white/60 border-b border-white/10 hover:text-gold transition-colors"
                      >
                        {m.label}
                      </Link>
                    ))}
                  </div>
                ))}
                <Link to="/quote" onClick={() => setOpen(false)} className="mt-6 bg-gold text-ink text-center py-3 font-semibold tracking-[0.15em] text-sm">GET A QUOTE</Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
